// src/components/admin/ImageUploader.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FiUpload,
  FiX,
  FiImage,
  FiCheck,
  FiAlertCircle,
  FiTrash2,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiUploadCloud,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

const MAX_IMAGES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function ImageUploader({
  images = [],
  onImagesChange,
  thumbnailIndex = -1,
  onThumbnailChange,
  maxImages = MAX_IMAGES,
  isUploading = false,
  disabled = false,
  onUploadImage,
  onDeleteImage,
  isEditing = false,
  uploadingImageId = null,
  onDeleteTempImage,
}) {
  const { actualTheme } = useTheme();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [imageItems, setImageItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize image items from props
  useEffect(() => {
    console.log('📸 [ImageUploader] Initializing with images:', images.length);
    const items = images.map((img, index) => {
      if (typeof img === 'string') {
        return {
          id: `existing_${index}`,
          url: img,
          status: 'uploaded',
          isExisting: true,
          order: index,
          uploadProgress: 100,
        };
      }
      const item = {
        id: img.id || `temp_${Date.now()}_${index}`,
        url: img.url || img.previewUrl || '',
        file: img.file,
        status: img.status || 'pending',
        isExisting: img.isExisting || false,
        order: img.order || index,
        uploadedUrl: img.uploadedUrl || null,
        publicId: img.publicId || null,
        uploadProgress: img.uploadProgress || 0,
        ratio: img.ratio || null,
        width: img.width || null,
        height: img.height || null,
      };
      console.log(`  📸 Image ${index}:`, {
        id: item.id,
        status: item.status,
        publicId: item.publicId,
        ratio: item.ratio,
        width: item.width,
        height: item.height,
        isExisting: item.isExisting,
      });
      return item;
    });
    setImageItems(items);
  }, [images]);

  // Update parent when items change
  const updateParentImages = useCallback(
    items => {
      console.log(
        '📤 [ImageUploader] Updating parent with items:',
        items.length,
      );
      const parentImages = items.map(item => ({
        id: item.id,
        url: item.url || item.previewUrl,
        file: item.file,
        status: item.status,
        isExisting: item.isExisting,
        uploadedUrl: item.uploadedUrl,
        publicId: item.publicId,
        order: item.order,
        uploadProgress: item.uploadProgress,
        ratio: item.ratio,
        width: item.width,
        height: item.height,
      }));
      onImagesChange?.(parentImages);
    },
    [onImagesChange],
  );

  const validateFile = file => {
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: `${file.name} is not an image file` };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `${file.name} exceeds 10MB limit` };
    }
    return { valid: true };
  };

  const handleFileSelect = files => {
    console.log('📥 [ImageUploader] File selected:', files.length);
    const validFiles = [];
    const newErrors = [];

    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        newErrors.push(validation.error);
      }
    }

    if (imageItems.length + validFiles.length > maxImages) {
      newErrors.push(`Maximum ${maxImages} images allowed`);
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setTimeout(() => setErrors([]), 5000);
    }

    if (validFiles.length > 0) {
      const newItems = [...imageItems];
      let newThumbnailIndex = thumbnailIndex;

      validFiles.forEach((file, index) => {
        const previewUrl = URL.createObjectURL(file);
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        newItems.push({
          id: tempId,
          file: file,
          url: previewUrl,
          previewUrl: previewUrl,
          status: 'pending',
          isExisting: false,
          order: newItems.length,
          uploadProgress: 0,
          ratio: null,
          width: null,
          height: null,
        });

        if (newItems.length === 1 && thumbnailIndex === -1) {
          newThumbnailIndex = 0;
        }
      });

      setImageItems(newItems);
      updateParentImages(newItems);
      if (newThumbnailIndex !== thumbnailIndex) {
        onThumbnailChange?.(newThumbnailIndex);
      }
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || isUploading) return;
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  const handleDragOver = e => {
    e.preventDefault();
    if (!disabled && !isUploading) setDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleUploadImage = async item => {
    console.log('⬆️ [ImageUploader] Uploading image:', item.id);
    if (item.status === 'uploaded' || item.status === 'uploading') return;
    if (!item.file) return;

    const updatedItems = imageItems.map(img =>
      img.id === item.id
        ? { ...img, status: 'uploading', uploadProgress: 0 }
        : img,
    );
    setImageItems(updatedItems);
    updateParentImages(updatedItems);

    try {
      if (onUploadImage) {
        let progress = 0;
        const progressInterval = setInterval(() => {
          progress += Math.random() * 10 + 5;
          if (progress >= 95) {
            progress = 95;
            clearInterval(progressInterval);
          }
          setImageItems(prev =>
            prev.map(img =>
              img.id === item.id
                ? { ...img, uploadProgress: Math.min(progress, 95) }
                : img,
            ),
          );
        }, 300);

        const result = await onUploadImage(item.file, item.id);
        console.log('✅ [ImageUploader] Upload result:', {
          url: result.url,
          publicId: result.publicId,
          ratio: result.ratio,
          width: result.width,
          height: result.height,
          format: result.format,
        });
        clearInterval(progressInterval);

        const finalItems = imageItems.map(img =>
          img.id === item.id
            ? {
                ...img,
                status: 'uploaded',
                uploadedUrl: result.url,
                publicId: result.publicId,
                uploadProgress: 100,
                url: result.url,
                ratio: result.ratio,
                width: result.width,
                height: result.height,
              }
            : img,
        );
        setImageItems(finalItems);
        updateParentImages(finalItems);

        if (thumbnailIndex === -1) {
          const idx = finalItems.findIndex(i => i.id === item.id);
          if (idx !== -1) {
            onThumbnailChange?.(idx);
          }
        }
      }
    } catch (error) {
      console.error('❌ [ImageUploader] Upload failed:', error);
      const revertedItems = imageItems.map(img =>
        img.id === item.id
          ? { ...img, status: 'pending', uploadProgress: 0 }
          : img,
      );
      setImageItems(revertedItems);
      updateParentImages(revertedItems);
      setErrors([`Failed to upload: ${error.message || 'Unknown error'}`]);
      setTimeout(() => setErrors([]), 5000);
    }
  };

  // FIXED: handleDeleteImage - prevents form submission
  const handleDeleteImage = async (item, e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('🔍 [ImageUploader] handleDeleteImage called with item:', {
      id: item.id,
      publicId: item.publicId,
      status: item.status,
      isExisting: item.isExisting,
      ratio: item.ratio,
    });

    setIsDeleting(true);
    try {
      const index = imageItems.findIndex(i => i.id === item.id);

      if (item.isExisting) {
        console.log('🔍 [ImageUploader] Deleting existing project image');
        if (onDeleteImage) {
          await onDeleteImage(index, false);
        }
        const newItems = imageItems.filter(i => i.id !== item.id);
        setImageItems(newItems);
        updateParentImages(newItems);
        return;
      }

      if (item.publicId && item.status === 'uploaded' && !item.isExisting) {
        console.log(
          `🔍 [ImageUploader] Deleting temporary uploaded image with publicId: ${item.publicId}`,
        );

        if (onDeleteTempImage) {
          console.log(
            `🔍 [ImageUploader] Calling onDeleteTempImage with: ${item.publicId}`,
          );
          await onDeleteTempImage(item.publicId);
          console.log(
            `✅ [ImageUploader] Successfully deleted temp image: ${item.publicId}`,
          );
        } else if (onDeleteImage) {
          console.log(
            `🔍 [ImageUploader] Falling back to onDeleteImage with publicId`,
          );
          await onDeleteImage(item.publicId, true);
        } else {
          console.warn(
            '⚠️ [ImageUploader] No delete function available for temp image',
          );
          setErrors(['No delete function available.']);
          setTimeout(() => setErrors([]), 5000);
          return;
        }

        const newItems = imageItems.filter(i => i.id !== item.id);
        setImageItems(newItems);
        updateParentImages(newItems);
        return;
      }

      console.log('🔍 [ImageUploader] Removing pending image from list');
      const newItems = imageItems.filter(i => i.id !== item.id);
      setImageItems(newItems);
      updateParentImages(newItems);

      if (thumbnailIndex >= newItems.length) {
        onThumbnailChange?.(newItems.length > 0 ? 0 : -1);
      } else if (thumbnailIndex === index) {
        onThumbnailChange?.(newItems.length > 0 ? 0 : -1);
      } else if (thumbnailIndex > index) {
        onThumbnailChange?.(thumbnailIndex - 1);
      }
    } catch (error) {
      console.error('❌ [ImageUploader] Delete error:', error);
      setErrors(['Failed to delete image. Please try again.']);
      setTimeout(() => setErrors([]), 5000);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(null);
    }
  };

  const setAsThumbnail = (index, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log(`👆 [ImageUploader] Setting thumbnail to index: ${index}`);
    if (disabled) return;
    onThumbnailChange?.(index);
  };

  const getImageUrl = item => {
    if (item.uploadedUrl) return item.uploadedUrl;
    if (item.url) return item.url;
    if (item.previewUrl) return item.previewUrl;
    return '';
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'uploaded':
        return <FiCheckCircle className="w-4 h-4 text-green-500" />;
      case 'uploading':
        return <FiRefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending':
        return <FiClock className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusLabel = status => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'uploading':
        return 'Uploading...';
      case 'pending':
        return 'Pending';
      default:
        return '';
    }
  };

  const pendingCount = imageItems.filter(i => i.status === 'pending').length;
  const uploadedCount = imageItems.filter(i => i.status === 'uploaded').length;

  // Log current state
  useEffect(() => {
    console.log('📊 [ImageUploader] Current state:', {
      totalImages: imageItems.length,
      uploaded: uploadedCount,
      pending: pendingCount,
      thumbnailIndex: thumbnailIndex,
      items: imageItems.map(i => ({
        id: i.id,
        status: i.status,
        ratio: i.ratio,
        width: i.width,
        height: i.height,
        isExisting: i.isExisting,
      })),
    });
  }, [imageItems, thumbnailIndex]);

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-6 transition-all ${
          dragOver
            ? 'border-blue-500 bg-blue-50/10'
            : 'border-gray-300 dark:border-gray-600'
        } ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
        onClick={() =>
          !disabled && !isUploading && fileInputRef.current?.click()
        }
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ borderColor: dragOver ? '#3b82f6' : 'var(--border)' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e => {
            if (e.target.files) {
              handleFileSelect(Array.from(e.target.files));
            }
            e.target.value = '';
          }}
          disabled={disabled || isUploading}
        />

        <div className="text-center">
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div
                className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin mb-3"
                style={{ borderColor: 'var(--text)' }}
              />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Saving project...
              </p>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <FiUpload
                  className="w-8 h-8 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                Drop images here or click to browse
              </p>
              <p
                className="text-xs mt-1 opacity-60"
                style={{ color: 'var(--muted)' }}
              >
                PNG, JPG, WebP up to 10MB each (max {maxImages} images)
              </p>
              <p
                className="text-xs mt-2 opacity-50"
                style={{ color: 'var(--muted)' }}
              >
                Each image needs to be uploaded individually before publishing
              </p>
            </>
          )}
        </div>
      </div>

      {/* Upload Stats */}
      {imageItems.length > 0 && (
        <div
          className="flex items-center gap-4 text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <span>Total: {imageItems.length}</span>
          {pendingCount > 0 && (
            <span className="text-yellow-500">⏳ {pendingCount} pending</span>
          )}
          {uploadedCount > 0 && (
            <span className="text-green-500">✓ {uploadedCount} uploaded</span>
          )}
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, idx) => (
            <p
              key={idx}
              className="text-xs text-red-500 flex items-center gap-1"
            >
              <FiAlertCircle className="w-3 h-3" />
              {error}
            </p>
          ))}
        </div>
      )}

      {/* Image Preview Grid */}
      {imageItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {imageItems.map((item, index) => {
            const isThumbnail = index === thumbnailIndex;
            const isUploadingItem = item.status === 'uploading';
            const isPending = item.status === 'pending';
            const isUploaded = item.status === 'uploaded';
            const imageUrl = getImageUrl(item);

            return (
              <div
                key={item.id}
                className={`group relative rounded-xl overflow-hidden border transition-all ${
                  isThumbnail ? 'ring-2 ring-blue-500' : ''
                }`}
                style={{
                  borderColor: isThumbnail ? '#3b82f6' : 'var(--border)',
                }}
              >
                {/* Image */}
                <div className="aspect-square relative bg-gray-100 dark:bg-gray-800">
                  <img
                    src={imageUrl}
                    alt={`Image ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={e => {
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                    }}
                  />

                  {/* Upload Progress Overlay */}
                  {isUploadingItem && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
                      <div
                        className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin mb-2"
                        style={{ borderColor: 'white' }}
                      />
                      <span className="text-xs text-white font-medium">
                        {Math.round(item.uploadProgress || 0)}%
                      </span>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-black/60 text-white backdrop-blur-sm">
                    {getStatusIcon(item.status)}
                    <span>{getStatusLabel(item.status)}</span>
                  </div>

                  {/* Thumbnail Badge */}
                  {isThumbnail && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-500 text-white">
                      Thumbnail
                    </div>
                  )}

                  {/* Image Order */}
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium bg-black/60 text-white">
                    #{index + 1}
                  </div>

                  {/* Actions Overlay */}
                  {!disabled && !isUploading && !isDeleting && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60">
                      {isPending && (
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUploadImage(item);
                          }}
                          className="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-all transform hover:scale-110"
                          title="Upload image"
                        >
                          <FiUploadCloud className="w-4 h-4" />
                        </button>
                      )}
                      {!isThumbnail && isUploaded && (
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAsThumbnail(index, e);
                          }}
                          className="p-2 rounded-full bg-green-500 hover:bg-green-600 text-white transition-all transform hover:scale-110"
                          title="Set as thumbnail"
                        >
                          <FiCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDeleteConfirm(item);
                        }}
                        className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all transform hover:scale-110"
                        title="Delete image"
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <FiTrash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* File Info for pending */}
                {isPending && item.file && (
                  <div
                    className="p-2 text-xs border-t"
                    style={{
                      borderColor: 'var(--border)',
                      color: 'var(--muted)',
                    }}
                  >
                    <p className="truncate">{item.file.name}</p>
                    <p>{(item.file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Image Count */}
      {imageItems.length > 0 && (
        <div
          className="flex justify-between items-center text-xs"
          style={{ color: 'var(--muted)' }}
        >
          <span>
            {imageItems.length} / {maxImages} images
          </span>
          <span>
            {thumbnailIndex >= 0
              ? `Thumbnail: Image #${thumbnailIndex + 1}`
              : 'No thumbnail set'}
          </span>
        </div>
      )}

      {/* Delete Confirmation Modal - FIXED to prevent form submission */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowDeleteConfirm(null);
          }}
        >
          <div
            className="relative w-full max-w-md rounded-xl p-6"
            style={{ backgroundColor: 'var(--bg)' }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <FiTrash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3
                className="text-lg font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                Delete Image
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                Are you sure you want to delete this image? This action cannot
                be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDeleteConfirm(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    await handleDeleteImage(showDeleteConfirm, e);
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {isDeleting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageUploader;
